import React, { FC, useEffect, useMemo, useState } from "react";
import { isCloud } from "../../constants/license-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { EnrolmentDevice } from "../../models/EnrolmentDevices";
import { EnrolmentTask } from "../../models/EnrolmentTask";
import { TaskOption } from "../../models/TaskOption";
import { useGlobalState } from "../../Providers";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { IShowEnrollValidOptions } from "../../services/wsPluginService/plugins/base/pluginModels";
import { useDialog } from "../dialog/DialogContext";
import List from '../list/list';
import { toast } from "react-toastify";

export const BIOMETRIC_TASK_CATEGORY_ID = 1000;
export const BIOMETRIC_TASK_CATEGORY_VALUE = 'fld_enrol_biometrics_category';
export const BIOMETRIC_BRAND_CONTROL_ID = 'biometric_brand_controlid';
export const BIOMETRIC_BRAND_IDEMIA = 'biometric_brand_idemia';
export const BIOMETRIC_BRAND_HID = 'biometric_brand_hid';
export const BIOMETRIC_BRAND_HIKVISION = 'biometric_brand_hikvision';

interface Props {
    onNext: (event: any) => void;
    availableSystemDeviceIds: number[];
    pluginDevice: IShowEnrollValidOptions;
    availabilityResolved?: boolean;
    isVingcardAccountAvailable?: boolean;
    mode?: 'main' | 'biometric-brand' | 'biometric-modality';
    selectedBiometricBrand?: string;
    onBack?: (event: any) => void;
    onSkip?: (event: any) => void;
    taskIdsToExclude?: number[];
}

const TasksForm: FC<Props> = ({
    onNext,
    availableSystemDeviceIds,
    pluginDevice,
    availabilityResolved = true,
    isVingcardAccountAvailable = false,
    mode = 'main',
    selectedBiometricBrand,
    onBack,
    onSkip,
    taskIdsToExclude = []
}) => {
    const [taskSelected, setTaskSelected] = useState(false);
    const [task, setTask] = useState('');
    const GlobalContext = useGlobalState();
    const { translate } = useLocale();
    const enrolmentTask = new EnrolmentTask(translate);
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [allEnrolmentTask, setAllEnrolmentTask] = useState<TaskOption[]>([
        enrolmentTask.ENROL_CARD,
        enrolmentTask.ENROL_QR_CODE,
        enrolmentTask.ENROL_MOBILE_CREDENTIAL,
        enrolmentTask.ENROL_FINGERPRINT,
        enrolmentTask.ENROL_HAND,
        enrolmentTask.ENROL_FACE,
        ...(isVingcardAccountAvailable ? [enrolmentTask.ENCODE_CARD] : [])
    ]);
    const dialog = useDialog();

    const isCloudMode = useMemo(() => {
        return isCloud(GlobalContext?.buildInfo.usage);
    }, [GlobalContext?.buildInfo.usage]);

    useEffect(() => {
        setEnrolmentTaskToShow();
    }, [isCloudMode, isVingcardAccountAvailable, mode, selectedBiometricBrand])

    const getBrandKey = (brandValue?: string): string => {
        if (brandValue === BIOMETRIC_BRAND_CONTROL_ID) return 'controlid';
        if (brandValue === BIOMETRIC_BRAND_IDEMIA) return 'idemia';
        if (brandValue === BIOMETRIC_BRAND_HID) return 'hid';
        if (brandValue === BIOMETRIC_BRAND_HIKVISION) return 'hikvision';
        return '';
    }

    const selectedBrandKey = getBrandKey(selectedBiometricBrand);
    const effectiveSystemDeviceIds = availableSystemDeviceIds;

    const setEnrolmentTaskToShow = async () => {
        try {
            const shouldShowBiometricsCategory = ![
                enrolmentTask.ENROL_FINGERPRINT.id,
                enrolmentTask.ENROL_HAND.id,
                enrolmentTask.ENROL_FACE.id,
            ].every((id) => taskIdsToExclude.includes(id));

            const tasks: TaskOption[] = mode === 'main'
                ? [
                    enrolmentTask.ENROL_CARD,
                    enrolmentTask.ENROL_QR_CODE,
                    enrolmentTask.ENROL_MOBILE_CREDENTIAL,
                    ...(isVingcardAccountAvailable ? [enrolmentTask.ENCODE_CARD] : []),
                    ...(shouldShowBiometricsCategory ? [{ id: BIOMETRIC_TASK_CATEGORY_ID, value: BIOMETRIC_TASK_CATEGORY_VALUE, label: translate.byKey(TranslationKeys.biometrics) }] : [])
                ]
                : mode === 'biometric-brand'
                    ? [
                        { id: 1101, value: BIOMETRIC_BRAND_CONTROL_ID, label: 'Control iD' },
                        { id: 1102, value: BIOMETRIC_BRAND_IDEMIA, label: 'Idemia' },
                        { id: 1103, value: BIOMETRIC_BRAND_HID, label: 'HID' },
                        { id: 1104, value: BIOMETRIC_BRAND_HIKVISION, label: 'Hikvision' }
                    ]
                    : [
                        ...(selectedBiometricBrand === BIOMETRIC_BRAND_HID ? [] : [enrolmentTask.ENROL_FINGERPRINT]),
                        ...(selectedBiometricBrand === BIOMETRIC_BRAND_IDEMIA ? [enrolmentTask.ENROL_HAND] : []),
                        enrolmentTask.ENROL_FACE,
                    ];

            setAllEnrolmentTask(tasks);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const getBiometricAvailability = () => {
        if (!availabilityResolved || isCloudMode) {
            return {
                finger: false,
                hand: false,
                face: false
            };
        }

        const availablePluginByBrand = (plugins: any[], brand: string) => {
            return plugins
                .filter((plugin) => plugin.brand === brand)
                .filter((plugin) => {
                    const isPluginReportedAvailable = plugin.type && pluginDevice?.hasOwnProperty(plugin.type) && pluginDevice[plugin.type] === true;
                    if (!isPluginReportedAvailable) {
                        return false;
                    }

                    if (plugin.requiredSystemDeviceIds && plugin.requiredSystemDeviceIds.length > 0) {
                        return plugin.requiredSystemDeviceIds.some((id) => effectiveSystemDeviceIds?.includes(id));
                    }

                    return true;
                })
                .length > 0;
        };

        const availableSystemByBrand = (devices: any[], brand: string) => {
            return devices
                .filter((device) => device.brand === brand)
                .filter(device => effectiveSystemDeviceIds?.includes(device.id))
                .length > 0;
        };

        const hasFingerForBrand = (brand: string) => {
            return availablePluginByBrand(enrolmentDevice.ENROL_FINGER_PLUGIN, brand) ||
                availableSystemByBrand(enrolmentDevice.ENROL_FINGER_DEVICES, brand);
        };

        const hasHandForBrand = (brand: string) => {
            return availablePluginByBrand(enrolmentDevice.ENROL_HAND_PLUGIN, brand) ||
                availableSystemByBrand(enrolmentDevice.ENROL_HAND_DEVICES, brand);
        };

        const hasFaceForBrand = (brand: string) => {
            return availablePluginByBrand(enrolmentDevice.ENROL_FACE_PLUGIN, brand) ||
                availableSystemByBrand(enrolmentDevice.ENROL_FACE_DEVICES, brand);
        };

        if (mode === 'biometric-brand' || mode === 'biometric-modality') {
            return {
                finger: selectedBrandKey ? hasFingerForBrand(selectedBrandKey) : false,
                hand: selectedBrandKey ? hasHandForBrand(selectedBrandKey) : false,
                face: selectedBrandKey ? hasFaceForBrand(selectedBrandKey) : false
            };
        }

        return {
            finger: enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FINGER_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FINGER_DEVICES, availableSystemDeviceIds).length !== 0,
            hand: enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_HAND_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_HAND_DEVICES, availableSystemDeviceIds).length !== 0,
            face: enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FACE_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FACE_DEVICES, availableSystemDeviceIds).length !== 0
        };
    };

    const isTaskAvailable = (taskValue: string): boolean => {
        const biometricAvailability = getBiometricAvailability();

        if (taskValue === BIOMETRIC_TASK_CATEGORY_VALUE) {
            return true;
        }

        if (mode === 'biometric-brand') {
            const hasBrandAvailability = (brandValue: string) => {
                const brandKey = getBrandKey(brandValue);
                if (!availabilityResolved || isCloudMode || !brandKey) {
                    return false;
                }

                const hasPlugin = (plugins: any[]) => plugins
                    .filter((plugin) => plugin.brand === brandKey)
                    .filter((plugin) => {
                        const isPluginReportedAvailable = plugin.type && pluginDevice?.hasOwnProperty(plugin.type) && pluginDevice[plugin.type] === true;
                        if (!isPluginReportedAvailable) {
                            return false;
                        }

                        if (plugin.requiredSystemDeviceIds && plugin.requiredSystemDeviceIds.length > 0) {
                            return plugin.requiredSystemDeviceIds.some((id) => effectiveSystemDeviceIds?.includes(id));
                        }

                        return true;
                    })
                    .length > 0;
                const hasSystem = (devices: any[]) => devices
                    .filter((device) => device.brand === brandKey)
                    .filter(device => effectiveSystemDeviceIds?.includes(device.id))
                    .length > 0;

                return hasPlugin(enrolmentDevice.ENROL_FINGER_PLUGIN) ||
                    hasSystem(enrolmentDevice.ENROL_FINGER_DEVICES) ||
                    hasPlugin(enrolmentDevice.ENROL_HAND_PLUGIN) ||
                    hasSystem(enrolmentDevice.ENROL_HAND_DEVICES) ||
                    hasPlugin(enrolmentDevice.ENROL_FACE_PLUGIN) ||
                    hasSystem(enrolmentDevice.ENROL_FACE_DEVICES);
            };

            if (taskValue === BIOMETRIC_BRAND_CONTROL_ID) {
                return hasBrandAvailability(taskValue);
            }
            if (taskValue === BIOMETRIC_BRAND_IDEMIA) {
                return hasBrandAvailability(taskValue);
            }
            if (taskValue === BIOMETRIC_BRAND_HID) {
                return hasBrandAvailability(taskValue);
            }
            if (taskValue === BIOMETRIC_BRAND_HIKVISION) {
                return hasBrandAvailability(taskValue);
            }
        }

        if (mode === 'biometric-modality') {
            if (taskValue === enrolmentTask.ENROL_FINGERPRINT.value) {
                return biometricAvailability.finger;
            }
            if (taskValue === enrolmentTask.ENROL_HAND.value) {
                return biometricAvailability.hand;
            }
            if (taskValue === enrolmentTask.ENROL_FACE.value) {
                return biometricAvailability.face;
            }
        }

        if (
            taskValue === enrolmentTask.ENROL_CARD.value ||
            taskValue === enrolmentTask.ENROL_QR_CODE.value ||
            taskValue === enrolmentTask.ENROL_MOBILE_CREDENTIAL.value ||
            taskValue === enrolmentTask.ENCODE_CARD.value
        ) {
            return true;
        }

        if (!availabilityResolved || isCloudMode) {
            return false;
        }

        if (taskValue === enrolmentTask.ENROL_FINGERPRINT.value) {
            return enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FINGER_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FINGER_DEVICES, effectiveSystemDeviceIds).length !== 0;
        }

        if (taskValue === enrolmentTask.ENROL_HAND.value) {
            return enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_HAND_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_HAND_DEVICES, effectiveSystemDeviceIds).length !== 0;
        }

        if (taskValue === enrolmentTask.ENROL_FACE.value) {
            return enrolmentDevice.getAvailablePlugins(enrolmentDevice.ENROL_FACE_PLUGIN, pluginDevice).length !== 0 ||
                enrolmentDevice.getSystemDevice(enrolmentDevice.ENROL_FACE_DEVICES, effectiveSystemDeviceIds).length !== 0;
        }

        return false;
    }

    const onLocalNext = (event: any) => {
        if (event.task) {
            // Task is not available. Meaning the account or device required to perform the enrolment
            // is one of the following: inactive, not installed, no plugins or device connected.
            if (!isTaskAvailable(event.task)) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.noEnrolmentDeviceFound) });
            }
            // Task is available. Meaning the account or device required to perform the enrolment is available for use.
            else {
                onNext({ ...event, id: event.id, task: event.task, description: event.description });
            }
        }
    }

    const itemSelected = (item: any) => {
        const selectedItem = item.props['data-item'];
        if (selectedItem) {
            if (!isTaskAvailable(selectedItem.value)) {
                return;
            }
            setTaskSelected(true);
            setTask(selectedItem.value);
            onLocalNext({id: selectedItem.id, task: selectedItem.value, description: selectedItem.label});
        }
    }

    const getListItems = (): { value: JSX.Element; isSelected: boolean; }[] => {
        return allEnrolmentTask
            .filter(item => !taskIdsToExclude.includes(item.id))
            .map(item => {
                const taskAvailable = isTaskAvailable(item.value);
                return {
                    value: (
                        <div
                            className={`flex flex-row items-center select-none ${taskAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                            data-item={item}>
                            <div id={`task-` + item.id} className="p-2">{item.label}</div>
                        </div>
                    ),
                    isSelected: item.value === task
                };
            });
    }

    return <div className="pt-3 pr-3 h-full relative">
        {!availabilityResolved ?
            <div className="mb-2 px-2 text-xs text-gray-500">{translate.byKey(TranslationKeys.loadingDevices)}</div>
            : null}
        <List onSelect={(_, item) => itemSelected(item)} isStriped items={getListItems()} />

        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            { onBack && 
                <div className="flex flex-row mt-4">
                    <button
                        className="secondary secondary-inverse py-1 px-5"
                        type="button"
                        onClick={onBack}>{translate.byKey(TranslationKeys.back)}
                    </button>
                </div>
            }

            { onSkip && 
                <div className="flex flex-row mt-4 pl-3">
                    <button
                        className="secondary secondary-inverse py-1 px-5"
                        type="button"
                        onClick={onSkip}>{translate.byKey(TranslationKeys.skip)}
                    </button>
                </div>
            }
            
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${taskSelected ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    onClick={(e) => onLocalNext(e)}>{translate.byKey(TranslationKeys.next)}
                </button>
            </div>
        </div>
    </div>
}

export default TasksForm;
